import { Component, OnDestroy, OnInit, Input, OnChanges, SimpleChanges } from '@angular/core';
import { Subject } from 'rxjs';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';

import { KpiDashboardField } from '../../../core/models/catalog.model';
import { DASHBOARD_FIELD_SCOPES, normalizeScope } from '../../../core/config/config.constants';
import { UtilsService } from '../../../core/services/utils.service';
import { CatalogService } from '../../../core/services/catalog.service';

import {
  faSpinner,
  faInfoCircle,
  faSearch,
  faTimes,
  faGripVertical,
  faTrash,
  faBan
} from '@fortawesome/free-solid-svg-icons';

export interface ScopeGroup {
  scope: string;
  items: KpiDashboardField[];
  normalItems: KpiDashboardField[];
  deletedItems: KpiDashboardField[];
  disabledItems: KpiDashboardField[];
}

@Component({
  selector: 'app-dashboard-config-selector',
  templateUrl: './dashboard-config-selector.component.html',
  styleUrls: ['./dashboard-config-selector.component.scss']
})
export class DashboardConfigSelectorComponent implements OnInit, OnDestroy, OnChanges {

  @Input() selectedAudience: 'USER' | 'CUSTOMER' = 'USER';
  @Input() dashboardFields: KpiDashboardField[] = [];

  private destroy$ = new Subject<void>();
  private dirtyItemKeys = new Set<string>();

  readonly SCOPES = DASHBOARD_FIELD_SCOPES;

  faSpinner = faSpinner;
  faInfoCircle = faInfoCircle;
  faSearch = faSearch;
  faTimes = faTimes;
  faGripVertical = faGripVertical;
  faTrash = faTrash;
  faBan = faBan;

  isLoading = false;
  kpiList: KpiDashboardField[] = [];
  scopeGroups: ScopeGroup[] = [];
  searchTerm: string = '';
  stylesheetString: string = 'aft';

  constructor(
    private utilsService: UtilsService,
    private catalogService: CatalogService
  ) {}

  ngOnInit(): void {
    this.stylesheetString = this.utilsService.getStylesheetString();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['dashboardFields']) {
      this.kpiList = [...(this.dashboardFields ?? [])];
      this.dirtyItemKeys.clear();
      this.applyFilter();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  applyFilter(): void {
    const term = this.searchTerm.toLowerCase().trim();
    // DASHBOARD_FIELD_SCOPES normalizzati e deduplicati, preservando l'ordine di prima apparizione.
    // SCOPE_NORMALIZATION_MAP raggruppa scope diversi sotto lo stesso scope normalizzato
    // (es. composite_kpi -> kpi), così le voci confluiscono in un unico gruppo come in signals-catalog20.
    const normalizedScopes: string[] = [];
    (DASHBOARD_FIELD_SCOPES as readonly string[]).forEach(s => {
      const n = normalizeScope(s);
      if (!normalizedScopes.includes(n)) normalizedScopes.push(n);
    });

    this.scopeGroups = normalizedScopes.map(scope => {
      const items = this.kpiList.filter(kpi => {
        if (normalizeScope(kpi.scope) !== scope) return false;
        if (!term) return true;
        return kpi.label.toLowerCase().includes(term) || kpi.name.toLowerCase().includes(term);
      });
      return {
        scope,
        items,
        normalItems: items.filter(k => !k.deletedFromCatalog && !k.disabledInCatalog),
        deletedItems: items.filter(k => k.deletedFromCatalog),
        disabledItems: items.filter(k => k.disabledInCatalog && !k.deletedFromCatalog)
      };
    }).filter(g => g.items.length > 0);
  }

  onSearchChange(): void { this.applyFilter(); }

  getScopeLabel(scope: string): string {
    return this.catalogService.getScopeLabel(scope);
  }

  /** Descrizione del gruppo (scopeDescriptions.*), mostrata nella "i" accanto al titolo */
  getScopeDescription(scope: string): string {
    return this.catalogService.getScopeDescription(scope);
  }

  get totalCount(): number {
    return this.kpiList.filter(k => !k.deletedFromCatalog && !k.disabledInCatalog).length;
  }

  get selectedCount(): number {
    return this.kpiList.filter(k => k.showInDashboard && !k.deletedFromCatalog && !k.disabledInCatalog).length;
  }

  get areAllSelected(): boolean {
    const normal = this.kpiList.filter(k => !k.deletedFromCatalog && !k.disabledInCatalog);
    return normal.length > 0 && normal.every(k => k.showInDashboard);
  }

  get isIndeterminate(): boolean {
    const normal = this.kpiList.filter(k => !k.deletedFromCatalog && !k.disabledInCatalog);
    const selected = normal.filter(k => k.showInDashboard).length;
    return selected > 0 && selected < normal.length;
  }

  groupSelectedCount(group: ScopeGroup): number {
    return group.normalItems.filter(k => k.showInDashboard).length;
  }

  groupAreAllSelected(group: ScopeGroup): boolean {
    return group.normalItems.length > 0 && group.normalItems.every(k => k.showInDashboard);
  }

  groupIsIndeterminate(group: ScopeGroup): boolean {
    const sel = group.normalItems.filter(k => k.showInDashboard).length;
    return sel > 0 && sel < group.normalItems.length;
  }

  private itemKey(kpi: KpiDashboardField): string {
    return kpi.uid ?? `${kpi.name}|${kpi.scope}`;
  }

  getModifiedCountForScope(scope: string): number {
    // scope qui è normalizzato (vedi applyFilter): confronto anche il raw scope dell'item normalizzato
    let count = 0;
    this.dirtyItemKeys.forEach(key => {
      const item = this.kpiList.find(i => this.itemKey(i) === key);
      if (item && normalizeScope(item.scope) === scope) count++;
    });
    return count;
  }

  toggleSelectAll(checked: boolean): void {
    this.kpiList.filter(k => !k.deletedFromCatalog && !k.disabledInCatalog).forEach(kpi => {
      kpi.showInDashboard = checked;
      this.dirtyItemKeys.add(this.itemKey(kpi));
    });
    this.applyFilter();
  }

  toggleSelectGroup(group: ScopeGroup, checked: boolean): void {
    group.normalItems.forEach(kpi => {
      kpi.showInDashboard = checked;
      this.dirtyItemKeys.add(this.itemKey(kpi));
    });
  }

  toggleDashboardVisibility(kpi: KpiDashboardField): void {
    kpi.showInDashboard = !kpi.showInDashboard;
    this.dirtyItemKeys.add(this.itemKey(kpi));
  }

  get hasPendingChanges(): boolean { return this.dirtyItemKeys.size > 0; }

  public resetDirtyState(): void {
    this.dirtyItemKeys.clear();
  }

  trackByUid(_index: number, kpi: KpiDashboardField): string {
    return kpi.uid ?? kpi.name;
  }

  onKpiDrop(event: CdkDragDrop<KpiDashboardField[]>, group: ScopeGroup): void {
    moveItemInArray(group.normalItems, event.previousIndex, event.currentIndex);
    group.items = [...group.normalItems, ...group.deletedItems, ...group.disabledItems];
    let globalOrder = 0;
    for (const g of this.scopeGroups) {
      g.normalItems.forEach((kpi: KpiDashboardField) => {
        kpi.order = globalOrder++;
        this.dirtyItemKeys.add(this.itemKey(kpi));
      });
    }
    this.syncKpiList();
  }

  private syncKpiList(): void {
    const allItems: KpiDashboardField[] = ([] as KpiDashboardField[]).concat(...this.scopeGroups.map(g => g.items));
    const allKeys = new Set(allItems.map((a: KpiDashboardField) => a.uid ?? a.name));
    this.kpiList = [
      ...allItems,
      ...this.kpiList.filter(k => !allKeys.has(k.uid ?? k.name))
    ];
  }

  public getDashboardConfiguration(): any {
    const selectedKpis = this.kpiList
      .filter(kpi => kpi.showInDashboard)
      .sort((a, b) => a.order - b.order)
      .map(kpi => ({ ...kpi }));
    return { dashboardConfig: selectedKpis };
  }
}
